import { ReactNode, useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import Typography from '@alltrails/core/components/Typography';
import Heading from '@alltrails/denali/components/Heading';
import { ModalAriaLabelledByContext } from '@alltrails/modal';
import Link from '@alltrails/denali/components/Link';
import Logo from '@alltrails/denali/components/Logo';
import LoginResponse from '../../types/LoginResponse';
import RegisterForm from '../RegisterForm';
import TermsOfService from '../TermsOfService';
import styles from './SignUpModal.module.scss';

// Desktop: https://www.figma.com/file/gZaqAej8HzoFkyrjlQ4cNs/Integrating-Brand-Changes?node-id=6514%3A99348
// Mobile: https://www.figma.com/file/gZaqAej8HzoFkyrjlQ4cNs/Integrating-Brand-Changes?node-id=6514%3A99372

type RegisterProps = {
  heading?: ReactNode;
  onLogInClick: () => void;
  onSuccess: ({ user }: LoginResponse) => void;
  grantType?: string | null;
  prefillEmail?: string;
  isConsolidatedAuth?: boolean;
  onEmailAlreadyExists?: (email: string) => void;
};

const Register = ({
  heading,
  onLogInClick,
  onSuccess,
  grantType,
  prefillEmail,
  isConsolidatedAuth,
  onEmailAlreadyExists
}: RegisterProps) => {
  const ariaLabelledBy = useContext(ModalAriaLabelledByContext);
  const defaultHeading =
    isConsolidatedAuth && prefillEmail ? (
      <FormattedMessage defaultMessage="Create a free account" />
    ) : (
      <FormattedMessage defaultMessage="To keep exploring trails, log in or sign up for free" />
    );

  return (
    <div className={styles.content}>
      <div className={styles.logo}>
        <Logo variant="symbol" size="lg" color="monochrome-dark" />
      </div>
      <Heading as="h4" id={ariaLabelledBy} size="400" className={styles.header}>
        {heading || defaultHeading}
      </Heading>
      <div className={styles.form}>
        <RegisterForm
          autoFocus
          handleRegistrationSuccess={onSuccess}
          grantType={grantType}
          prefillEmail={prefillEmail}
          onEmailAlreadyExists={onEmailAlreadyExists}
          isConsolidatedAuth={isConsolidatedAuth}
        />
      </div>
      {!isConsolidatedAuth && (
        <Typography component="div" mb="32" variant="paragraph200" color="secondary">
          <FormattedMessage defaultMessage="Already have an account?" />{' '}
          <Link size="md" onClick={onLogInClick}>
            <FormattedMessage defaultMessage="Log in" />
          </Link>
        </Typography>
      )}
      <TermsOfService />
    </div>
  );
};

export default Register;
